/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  CircularProgress,
  ClickAwayListener,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';

import { useTheme } from 'contexts/ThemeContext';

import { ButtonCreate } from 'components/ButtonCreate';
import { TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';

export const EntityLinker = ({
  fetchOptions,
  onSelect,
  renderOption,
  placeholder = 'Rechercher...',
  pageSize = 10
}) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const { themeColors } = useTheme();

  const listRef = useRef(null);

  const loadOptions = async (searchTerm, pageNumber = 1) => {
    setLoading(true);
    try {
      const result = await fetchOptions({
        search: searchTerm,
        page: pageNumber,
        limit: pageSize
      });
      if (pageNumber === 1) {
        setOptions(result.data);
      } else {
        setOptions(prev => [...prev, ...result.data]);
      }
      setHasMore(result.hasMore);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    loadOptions(search, 1);
  }, [search]);

  const handleScroll = () => {
    const list = listRef.current;
    if (!list || loading || !hasMore) {
      return;
    }
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 10) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadOptions(search, nextPage);
    }
  };

  return (
    <Container>
      <Row spacing={1}>
        <Item justify="flex-start">
          <TextField
            fullWidth
            placeholder={placeholder}
            value={search}
            type="search"
            onChange={e => setSearch(e)}
            unit={
              <SearchIcon
                style={{
                  color: themeColors.light
                }}
              />
            }
          />
        </Item>
      </Row>
      <Row>
        <Item justify="flex-start">
          <List
            ref={listRef}
            style={{ maxHeight: 250, overflow: 'auto' }}
            onScroll={handleScroll}
          >
            {options.map(item => (
              <ListItem
                key={item._id || item.id || item.key}
                button
                onClick={() => onSelect(item)}
              >
                {renderOption
                  ? renderOption(item)
                  : item.name || item.label || 'Unknown'}
              </ListItem>
            ))}
            {loading && (
              <ListItem>
                <CircularProgress size={20} />
              </ListItem>
            )}
            {!loading && options.length === 0 && (
              <ListItem>Aucun résultat</ListItem>
            )}
          </List>
        </Item>
      </Row>
    </Container>
  );
};

const TableField = ({
  headers,
  fetchPage,
  rowsPerPageOptions = [5, 10, 25, 50],
  defaultRowsPerPage = 10,
  renderRow,
  enableLinker = false,
  fetchLinkerOptions,
  onLinkerSelect,
  renderLinkerOption,
  rowsOverride,
  onDelete
}) => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLinker, setShowLinker] = useState(false);
  const { themeColors } = useTheme();
  const buttonRef = useRef(null);
  const linkerRowRef = useRef(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await fetchPage({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm
      });
      setRows(resp.data || []);
      setTotalCount(resp.totalCount || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, rowsPerPage, searchTerm]);

  const handleDelete = row => {
    setRows(prev => prev.filter(r => r._id !== row._id || r.id !== row.id));
    setTotalCount(prev => prev - 1);
    if (onDelete) {
      onDelete(row.id);
    }
  };

  const handleClickAway = event => {
    if (buttonRef.current && buttonRef.current.contains(event.target)) {
      return;
    }
    if (linkerRowRef.current && linkerRowRef.current.contains(event.target)) {
      return;
    }
    setShowLinker(false);
  };

  return (
    <Paper style={{ width: '100%', overflowX: 'auto', padding: 16 }}>
      <Item justify="flex-start" xs={2}>
        <TextField
          width="300px"
          placeholder="Rechercher..."
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e);
            setPage(0);
          }}
          style={{ marginBottom: 16 }}
          type="search"
          unit={
            <SearchIcon
              style={{
                color: themeColors.light
              }}
            />
          }
        />
      </Item>
      <Table style={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            {headers.map(header => (
              <TableCell
                key={header.id}
                style={{
                  width: `${100 / headers.length}%`,
                  textAlign: 'center'
                }}
              >
                {header.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {(() => {
            if (loading)
              return (
                <TableRow>
                  <TableCell
                    colSpan={headers.length + (onDelete ? 1 : 0)}
                    style={{ textAlign: 'center' }}
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              );
            if ((rowsOverride || rows).length === 0)
              return (
                <TableRow>
                  <TableCell
                    colSpan={headers.length + (onDelete ? 1 : 0)}
                    style={{ textAlign: 'center' }}
                  >
                    Aucun résultat
                  </TableCell>
                </TableRow>
              );

            return (rowsOverride || rows).map(row => (
              <TableRow key={row._id || row.id || JSON.stringify(row)}>
                {headers.map(header => (
                  <TableCell
                    key={header.id}
                    style={{
                      width: `${100 / headers.length}%`,
                      textAlign: 'center'
                    }}
                  >
                    {renderRow
                      ? renderRow(row, header, handleDelete)
                      : row[header.id]}
                  </TableCell>
                ))}
              </TableRow>
            ));
          })()}
        </TableBody>

        <TableFooter>
          <TableRow>
            <TableCell
              colSpan={headers.length + (onDelete ? 1 : 0)}
              style={{ padding: 0 }}
            >
              <Row justify="center">
                <TablePagination
                  rowsPerPageOptions={rowsPerPageOptions}
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={event => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  component="div"
                />
              </Row>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
      {enableLinker && fetchLinkerOptions && onLinkerSelect && (
        <Container style={{ marginTop: 16 }}>
          <Row justify="flex-start" spacing={1}>
            <Item justify="flex-start" xs={2}>
              {!showLinker && (
                <ButtonCreate
                  ref={buttonRef}
                  justify="flex-start"
                  onClick={() => setShowLinker(true)}
                >
                  Lier une organisation
                </ButtonCreate>
              )}
            </Item>
          </Row>
          {showLinker && (
            <ClickAwayListener onClickAway={handleClickAway}>
              <Row
                ref={linkerRowRef}
                justify="flex-start"
                spacing={1}
                style={{ marginTop: 8 }}
              >
                <Item justify="flex-start" xs={2}>
                  <EntityLinker
                    fetchOptions={fetchLinkerOptions}
                    onSelect={item => {
                      setRows(prev => [item, ...prev]);
                      setTotalCount(prev => prev + 1);
                      setShowLinker(false);
                      if (onLinkerSelect) {
                        onLinkerSelect(item);
                      }
                    }}
                    renderOption={renderLinkerOption}
                  />
                </Item>
              </Row>
            </ClickAwayListener>
          )}
        </Container>
      )}
    </Paper>
  );
};

export default TableField;
